import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getSettingsRequest } from "../../../redux/settings/settingsSlice";
import { saveBirthdayGiftRequest } from "../../../redux/settings/settingsSlice";
import { getMetalsRequest } from "../../../redux/GetMetals/metalSlice";
import AccessControl from "../../Comman/AccessControl";
import { controlAllFeatureOptions } from "../../../configs/constants";
import { canShowPermission } from "../Permissions/permissionObject";

const BirthdayGift = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.settingData);
  const decimalPrecision =
    useSelector((state) => state?.appConfig?.configData?.decimalPrecision) ?? 4;
  const metalsdata = data?.data?.metals || [];

  const [isEnabled, setIsEnabled] = useState(false);
  const [metal, setMetal] = useState("");
  const [weight, setWeight] = useState("");
  const [validityDays, setValidityDays] = useState("");
  const [type, setType] = useState("weight");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSavingbirthgift, setIsSavingBirthgift] = useState(false);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreateSetting = canShowPermission(permissions, "birthdayGift", "create");
  const canEdit = canShowPermission(permissions, "birthdayGift", "edit");

  useEffect(() => {
    dispatch(getSettingsRequest());
    dispatch(getMetalsRequest());
  }, [dispatch]);

  useEffect(() => {
    const giftingData = data?.data?.birthdaygiftdetails || {};
    setIsEnabled(giftingData?.isEnabled || false);
    setWeight(giftingData?.weight ?? giftingData?.amount ?? "");
    setValidityDays(giftingData?.validityDays || "");
    setMetal(giftingData?.metal || "");
    setType(giftingData?.type || "weight");
    setStartDate(
      giftingData?.startDate ? new Date(giftingData.startDate) : null
    );
    setEndDate(giftingData?.endDate ? new Date(giftingData.endDate) : null);
  }, [data]);

  const handleSaveBirthDayDetails = async () => {
    if (isSavingbirthgift) return;

    const trimmedWeight = String(weight).trim();
    const trimmedValidityDays = String(validityDays).trim();
    const trimmedMetal = String(metal).trim();
    const trimmedType = String(type).trim();

    if (startDate && endDate && endDate < startDate) {
      toast.error("End date cannot be earlier than start date.");
      return;
    }

    if (!trimmedWeight) {
      toast.error(
        trimmedType === "value" ? "Amount is required." : "Weight is required."
      );
      return;
    }
    if (Number(trimmedWeight) <= 0) {
      toast.error(
        trimmedType === "value"
          ? "Amount must be greater than 0."
          : "Weight must be greater than 0."
      );
      return;
    }
    if (!trimmedValidityDays) {
      toast.error("Validity days are required.");
      return;
    }
    if (!trimmedMetal) {
      toast.error("Please select a metal.");
      return;
    }
    if (!trimmedType) {
      toast.error("Please select type");
      return;
    }

    setIsSavingBirthgift(true);

    const payload = {
      key: "birthdaygiftdetails",
      value: {
        isEnabled,
        ...(trimmedType === "value"
          ? { amount: trimmedWeight }
          : { weight: trimmedWeight }),
        validityDays: trimmedValidityDays,
        metal: trimmedMetal,
        type: trimmedType,
        startDate,
        endDate,
      },
    };

    try {
      await dispatch(saveBirthdayGiftRequest(payload));
    } catch (err) {
      console.error("Failed to save:", err);
      toast.error("Failed to save birthday gift details.");
    } finally {
      setTimeout(() => {
        setIsSavingBirthgift(false);
      }, 2500);
    }
  };

  return (
    <AccessControl feature={controlAllFeatureOptions.Birthday_Gift}>
      <Box sx={{ width: "100%", mt: { xs: 10, md: 10 }, mb: { xs: 2, md: 2 } }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            width: "100%",
            border: "1px solid #ccc",
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={24}
            mb={3}
            fontFamily={"Aeonik"}
          >
            Gifting on birthday
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
            }
            label="Enable functionality"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label={
                  <>
                    {type === "value" ? "Enter Amount" : "Enter Weight"}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </>
                }
                value={weight}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/[^0-9.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) {
                    value = parts[0] + "." + parts[1];
                  }
                  if (
                    value.startsWith("0") &&
                    !value.startsWith("0.") &&
                    value.length > 1
                  ) {
                    value = value.replace(/^0+/, "");
                  }
                  const maxDecimals =
                    type === "value" ? 2 : Math.max(0, Number(decimalPrecision) || 4);
                  if (parts[1]?.length > maxDecimals) {
                    value = parts[0] + "." + parts[1].slice(0, maxDecimals);
                  }
                  setWeight(value);
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label={
                  <>
                    Validity Days <span style={{ color: "red" }}>*</span>
                  </>
                }
                value={validityDays}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/\D/g, "");
                  value = value.replace(/^0+/, "");
                  setValidityDays(value);
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>
                  Select Metal <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  value={metal}
                  label="Select Metal"
                  onChange={(e) => setMetal(e.target.value)}
                >
                  {(metalsdata || [])
                    .filter((metal) => metal.status === true)
                    .map((metalItem) => (
                      <MenuItem key={metalItem._id} value={metalItem._id}>
                        {metalItem.metalName} ({metalItem.karat}k -{" "}
                        {metalItem.purity})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>
                  Select Type <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  value={type}
                  label="Select Type"
                  onChange={(e) => setType(e.target.value)}
                >
                  <MenuItem value="weight">Weight</MenuItem>
                  <MenuItem value="value">Value</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  sx={{ width: "100%" }}
                  label="Start Offer Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="dd/MM/yyyy hh:mm a"
                  renderInput={(params) => (
                    <TextField fullWidth {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  sx={{ width: "100%" }}
                  label="End Offer Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="dd/MM/yyyy hh:mm a"
                  renderInput={(params) => (
                    <TextField fullWidth {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Box mt={4}>
            {(canCreateSetting || canEdit) && (
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#752ADA",
                  px: 6,
                  textTransform: "none",
                }}
                onClick={handleSaveBirthDayDetails}
                disabled={isSavingbirthgift}
              >
                {isSavingbirthgift ? "Saving..." : "Save"}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </AccessControl>
  );
};

export default BirthdayGift;
